import { useCallback, useEffect, useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import type { AdminVehicle } from './types'
import { createVehicle, deleteVehicle, fetchVehicles, updateVehicle } from './useAdminApi'
import { SettingsFormModal } from './SettingsFormModal'
import { AdminTable, Banner, Btn, Field, SectionHeader, Td, Th, inputCls } from './ui'

const EMPTY: Partial<AdminVehicle> = {
  vehicle_id: '', depot: '', route_id: '', driver_name: '', driver_phone: '',
}

export function VehiclesSection({ configured }: { configured: boolean }) {
  const [rows, setRows] = useState<AdminVehicle[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [banner, setBanner] = useState<{ tone: 'ok' | 'error'; msg: string } | null>(null)
  const [editing, setEditing] = useState<Partial<AdminVehicle> | null>(null)
  const [isNew, setIsNew] = useState(false)

  const load = useCallback(async () => {
    if (!configured) { setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      setRows(await fetchVehicles())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load vehicles')
    } finally {
      setLoading(false)
    }
  }, [configured])

  useEffect(() => { void load() }, [load])

  const openNew = () => { setEditing({ ...EMPTY }); setIsNew(true) }
  const openEdit = (v: AdminVehicle) => { setEditing({ ...v }); setIsNew(false) }

  const save = async () => {
    if (!editing?.vehicle_id?.trim()) {
      setBanner({ tone: 'error', msg: 'Vehicle ID is required' })
      return
    }
    try {
      if (isNew) await createVehicle(editing)
      else await updateVehicle(editing.vehicle_id, editing)
      setBanner({ tone: 'ok', msg: isNew ? 'Vehicle created' : 'Vehicle updated' })
      setEditing(null)
      await load()
    } catch (e) {
      setBanner({ tone: 'error', msg: e instanceof Error ? e.message : 'Save failed' })
    }
  }

  const remove = async (id: string) => {
    if (!window.confirm(`Delete vehicle ${id}?`)) return
    try {
      await deleteVehicle(id)
      setBanner({ tone: 'ok', msg: 'Vehicle deleted' })
      await load()
    } catch (e) {
      setBanner({ tone: 'error', msg: e instanceof Error ? e.message : 'Delete failed' })
    }
  }

  if (!configured) {
    return (
      <p className="text-sm text-slate-500">
        The reporting database is not configured. Editing the vehicle roster needs it connected.
      </p>
    )
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Vehicles"
        subtitle="Fleet register — driver, route, depot and device assignment."
        action={<Btn onClick={openNew}><span className="inline-flex items-center gap-1"><Plus size={14} /> Add vehicle</span></Btn>}
      />
      {banner ? <Banner tone={banner.tone} onDismiss={() => setBanner(null)}>{banner.msg}</Banner> : null}
      {error ? <Banner tone="error">{error}</Banner> : null}
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : (
        <AdminTable>
          <thead>
            <tr>
              <Th>Vehicle</Th>
              <Th>Driver</Th>
              <Th>Phone</Th>
              <Th>Depot</Th>
              <Th>Route</Th>
              <Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((v) => (
              <tr key={v.vehicle_id} className="hover:bg-slate-50/80">
                <Td mono>{v.vehicle_id}</Td>
                <Td>{v.driver_name ?? '—'}</Td>
                <Td mono>{v.driver_phone ?? '—'}</Td>
                <Td>{v.depot ?? '—'}</Td>
                <Td mono className="max-w-[140px] truncate">{v.route_id ?? '—'}</Td>
                <Td>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => openEdit(v)} className="rounded p-1 text-slate-500 hover:bg-slate-100" title="Edit">
                      <Pencil size={14} />
                    </button>
                    <button type="button" onClick={() => void remove(v.vehicle_id)} className="rounded p-1 text-rose-500 hover:bg-rose-50" title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
            {!rows.length ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-slate-400">No vehicles in roster</td></tr>
            ) : null}
          </tbody>
        </AdminTable>
      )}

      <SettingsFormModal
        open={editing !== null}
        title={isNew ? 'New vehicle' : `Edit ${editing?.vehicle_id ?? ''}`}
        onClose={() => setEditing(null)}
        onSave={() => void save()}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Vehicle ID">
            <input className={inputCls} value={editing?.vehicle_id ?? ''} disabled={!isNew}
              onChange={(e) => setEditing({ ...editing!, vehicle_id: e.target.value })} />
          </Field>
          <Field label="Device ID">
            <input className={inputCls} value={editing?.device_id ?? ''} onChange={(e) => setEditing({ ...editing!, device_id: e.target.value })} />
          </Field>
          <Field label="Driver name">
            <input className={inputCls} value={editing?.driver_name ?? ''} onChange={(e) => setEditing({ ...editing!, driver_name: e.target.value })} />
          </Field>
          <Field label="Driver phone">
            <input className={inputCls} value={editing?.driver_phone ?? ''} onChange={(e) => setEditing({ ...editing!, driver_phone: e.target.value })} />
          </Field>
          <Field label="Depot">
            <input className={inputCls} value={editing?.depot ?? ''} onChange={(e) => setEditing({ ...editing!, depot: e.target.value })} />
          </Field>
          <Field label="Route ID">
            <input className={inputCls} value={editing?.route_id ?? ''} onChange={(e) => setEditing({ ...editing!, route_id: e.target.value })} />
          </Field>
          <Field label="Fleet">
            <input className={inputCls} value={editing?.fleet ?? ''} onChange={(e) => setEditing({ ...editing!, fleet: e.target.value })} />
          </Field>
          <Field label="Model">
            <input className={inputCls} value={editing?.model ?? ''} onChange={(e) => setEditing({ ...editing!, model: e.target.value })} />
          </Field>
          <Field label="Capacity (kWh)">
            <input className={inputCls} type="number" value={editing?.capacity_kwh ?? ''}
              onChange={(e) => setEditing({ ...editing!, capacity_kwh: e.target.value ? Number(e.target.value) : null })} />
          </Field>
        </div>
      </SettingsFormModal>
    </div>
  )
}
